import type { LucideIcon } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

interface CommandPaletteState {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (next: boolean) => void;
}

/** A command the current page offers in ⌘K (e.g. "Approve this draft"). */
export interface ContextCommand {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Shown on the right, e.g. "A". Display only — the page owns the key. */
  shortcut?: string;
  /** Extra words cmdk matches on. */
  keywords?: string[];
  run: () => void;
}

export interface ContextCommandGroup {
  heading: string;
  commands: ContextCommand[];
}

const CommandPaletteContext = createContext<CommandPaletteState | undefined>(undefined);
/** What pages registered — read by the palette only. */
const ContextCommandsContext = createContext<ContextCommandGroup[]>([]);
/** The registrar — a stable function, so registering never re-renders the page. */
const RegisterCommandsContext = createContext<
  ((key: string, group: ContextCommandGroup | null) => void) | undefined
>(undefined);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [registered, setRegistered] = useState<Record<string, ContextCommandGroup>>({});

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const register = useCallback((key: string, group: ContextCommandGroup | null) => {
    setRegistered((prev) => {
      if (group === null) {
        if (!(key in prev)) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: group };
    });
  }, []);

  const value = useMemo<CommandPaletteState>(
    () => ({ open, setOpen, toggle, shortcutsOpen, setShortcutsOpen }),
    [open, shortcutsOpen, toggle],
  );
  const groups = useMemo(() => Object.values(registered), [registered]);

  return (
    <CommandPaletteContext value={value}>
      <RegisterCommandsContext value={register}>
        <ContextCommandsContext value={groups}>{children}</ContextCommandsContext>
      </RegisterCommandsContext>
    </CommandPaletteContext>
  );
}

export function useCommandPalette(): CommandPaletteState {
  const ctx = use(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within a <CommandPaletteProvider>');
  }
  return ctx;
}

/** The groups pages registered, for the palette to render. */
export function useContextCommands(): ContextCommandGroup[] {
  return use(ContextCommandsContext);
}

/**
 * Offer page-specific commands in ⌘K while the calling component is mounted.
 *
 * Re-registers only when what's SHOWN changes (ids, labels, shortcuts) — the
 * `run` callbacks are read through a ref at call time, so passing fresh
 * closures every render never loops. Unregisters on unmount.
 */
export function useRegisterCommands(heading: string, commands: ContextCommand[]): void {
  const register = use(RegisterCommandsContext);
  const key = useId();
  const latest = useRef(commands);
  latest.current = commands;
  const signature = commands
    .map(
      (c) =>
        `${c.id}\u0000${c.label}\u0000${c.shortcut ?? ''}\u0000${(c.keywords ?? []).join(' ')}`,
    )
    .join('\u0001');

  // biome-ignore lint/correctness/useExhaustiveDependencies: `signature` is the change detector for `commands`; the callbacks are read through `latest` at call time.
  useEffect(() => {
    if (!register) return;
    register(key, {
      heading,
      commands: latest.current.map((c) => ({
        ...c,
        run: () => latest.current.find((x) => x.id === c.id)?.run(),
      })),
    });
  }, [register, key, heading, signature]);

  useEffect(() => {
    if (!register) return undefined;
    return () => register(key, null);
  }, [register, key]);
}
